import find from 'lodash/find';
import toLower from 'lodash/toLower';
import isEqual from 'lodash/isEqual';

import * as LoanFormatActionsTypes from './types';
import { LoanFormatChange } from './types';
import { LoanFormatState } from './state';
import { AppDispatch } from '../index';

export enum LoanFormatKeys {
    LOANFORMAT_EAUDIOBOOKS = 'LOANFORMAT_EAUDIOBOOKS',
    LOANFORMAT_EBOOKS = 'LOANFORMAT_EBOOKS',
    LOANFORMAT_EPRESS = 'LOANFORMAT_EPRESS',
    LOANFORMAT_ALL = 'LOANFORMAT_ALL'
}

export const LOANFORMAT_EAUDIOBOOKS = 'EAUDIOBOOKS';
export const LOANFORMAT_EBOOKS = 'EBOOKS';
export const LOANFORMAT_EPRESS = 'EPRESS';
export const LOANFORMAT_ALL = 'ALL';

export const URL_LOANFORMAT_EAUDIOBOOKS = 'eaudiobooks';
export const URL_LOANFORMAT_EBOOKS = 'ebooks';
export const URL_LOANFORMAT_EPRESS = 'epress';
export const URL_LOANFORMAT_EPRESS_ISSUES = 'emagazineissues';
export const URL_LOANFORMAT_ALL = 'all';

export const LOANFORMAT_ALL_OBJECT: LoanFormatState = {
    // the backend expects no loanFormat query param as the default which shows all objects
    // that might be the reason why there is this split between the key and requestKey
    requestKey: null,
    key: LOANFORMAT_ALL,
    name: 'All Formats',
    singularName: null,
    icon: 'star-outline',
    urlKey: URL_LOANFORMAT_ALL
};
export const LOANFORMAT_EBOOK_OBJECT: LoanFormatState = {
    requestKey: LOANFORMAT_EBOOKS,
    key: LOANFORMAT_EBOOKS,
    name: 'eBooks',
    singularName: 'eBook',
    icon: 'ebook-filled',
    urlKey: URL_LOANFORMAT_EBOOKS
};
export const LOANFORMAT_EAUDIOBOOK_OBJECT: LoanFormatState = {
    requestKey: LOANFORMAT_EAUDIOBOOKS,
    key: LOANFORMAT_EAUDIOBOOKS,
    name: 'eAudiobooks',
    singularName: 'eAudiobook',
    icon: 'eaudiobook-filled',
    urlKey: URL_LOANFORMAT_EAUDIOBOOKS
};
export const LOANFORMAT_EPRESS_OBJECT: LoanFormatState = {
    requestKey: LOANFORMAT_EPRESS,
    key: LOANFORMAT_EPRESS,
    name: 'ePress',
    singularName: 'ePress',
    icon: 'emagazine-filled',
    urlKey: URL_LOANFORMAT_EPRESS
};

/**
 * @deprectaed
 */
export const LOANFORMAT_EMAGAZINES_OBJECT: LoanFormatState = {
    requestKey: 'EMAGAZINES',
    key: 'EMAGAZINES',
    name: 'eMagazines',
    singularName: 'eMagazine',
    icon: 'emagazine-filled',
    urlKey: 'emagazines'
};

// FIXME Should be obsolete with typescript
export function isLoanFormatObject(value: any): value is LoanFormatState {
    return isEbookObject(value) || isEaudiobookObject(value) || isEpressObject(value) || isLoanFormatAllObject(value);
}

export function isLoanFormatKey(value: any): value is string {
    return isEbookKey(value) || isEaudiobookKey(value) || isEpressKey(value) || isLoanFormatAllKey(value);
}

export function isConcreteLoanFormatObject(value: LoanFormatState) {
    return isEaudiobookObject(value) || isEbookObject(value) || isEpressObject(value);
}

export function isEaudiobookKey(value: string) {
    //TODO: replace by more strict check without toLower (eaudioBooks would return true, but key is EAUDIOBOOKS)
    return toLower(value) === toLower(LOANFORMAT_EAUDIOBOOKS);
}

export function isEbookKey(value: string) {
    //TODO: replace by more strict check without toLower (eBooks would return true, but key is EBOOKS)
    return toLower(value) === toLower(LOANFORMAT_EBOOKS);
}

export function isEpressKey(value: string) {
    //TODO: replace by more strict check without toLower (eMagazines would return true, but key is EMAGAZINES)
    return toLower(value) === toLower(LOANFORMAT_EPRESS);
}

// FIXME Those funtions should be obsolete with typescript
export function isEbookObject(value: any): boolean {
    return isEqual(value, LOANFORMAT_EBOOK_OBJECT);
}

export function isEaudiobookObject(value: any): boolean {
    return isEqual(value, LOANFORMAT_EAUDIOBOOK_OBJECT);
}

export function isEpressObject(value: any): boolean {
    return isEqual(value, LOANFORMAT_EPRESS_OBJECT);
}

export function isLoanFormatAllObject(value: any): boolean {
    return isEqual(value, LOANFORMAT_ALL_OBJECT);
}

export function isLoanFormatAllKey(value: string) {
    //TODO: replace by more strict check without toLower (all would return true, but key is ALL)
    return toLower(value) === toLower(LOANFORMAT_ALL);
}

export function getConcreteLoanFormatStates(): LoanFormatState[] {
    return [LOANFORMAT_EAUDIOBOOK_OBJECT, LOANFORMAT_EBOOK_OBJECT, LOANFORMAT_EPRESS_OBJECT];
}

export function getLoanFormatStates(): LoanFormatState[] {
    return [LOANFORMAT_EAUDIOBOOK_OBJECT, LOANFORMAT_EBOOK_OBJECT, LOANFORMAT_EPRESS_OBJECT, LOANFORMAT_ALL_OBJECT];
}

// FIXME We can use LoanFormatState() directly imho
export function getCollectionLoanFormats(): LoanFormatState[] {
    return getLoanFormatStates();
}

export function loanFormatKeyToObject(key: string): LoanFormatState {
    return find(getLoanFormatStates(), val => toLower(key) === toLower(val.key));
}

export function concreteLoanFormatKeyToName(key?: string): string {
    const loanFormat = find(getConcreteLoanFormatStates(), { key });
    return loanFormat?.name ?? '';
}

export function concreteLoanFormatNameToKey(name?: string | null): string {
    const loanFormat = find([...getConcreteLoanFormatStates(), LOANFORMAT_EMAGAZINES_OBJECT], { name });

    if (loanFormat?.key === LOANFORMAT_EMAGAZINES_OBJECT.key) {
        return LOANFORMAT_EPRESS_OBJECT.key;
    } else {
        return loanFormat?.key ?? '';
    }
}

export function concreteLoanFormatNameToObject(name: string): LoanFormatState | undefined {
    const result = find([...getConcreteLoanFormatStates(), LOANFORMAT_EMAGAZINES_OBJECT], { name });

    if (result && result.key === LOANFORMAT_EMAGAZINES_OBJECT.key) {
        return LOANFORMAT_EPRESS_OBJECT;
    } else {
        return result;
    }
}

export function concreteLoanFormatKeyToObject(key: string): LoanFormatState | undefined {
    const result = find([...getConcreteLoanFormatStates(), LOANFORMAT_EMAGAZINES_OBJECT], { key });

    if (result && result.key === LOANFORMAT_EMAGAZINES_OBJECT.key) {
        return LOANFORMAT_EPRESS_OBJECT;
    } else {
        return result;
    }
}

const loanFormatChangeAction = (loanFormat: LoanFormatState): LoanFormatChange => {
    return {
        type: LoanFormatActionsTypes.LOANFORMAT_CHANGE,
        payload: loanFormat
    };
};

export function changeLoanFormat(loanFormat: LoanFormatState) {
    return (dispatch: AppDispatch) => {
        dispatch(loanFormatChangeAction(loanFormat));
    };
}
